import { Grid, Skeleton, Stack, Typography } from "@mui/material";
import { JsonSchemaRoot } from "components/JsonSchema/types";
import JsonSchemaPathDropdownPicker from "components/JsonSchema/JsonSchemaPathDropdownPicker";
import { Question } from "components/Admin/Organizations/types/Module.type";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import React from "react";
import { MetadataType } from "models/MetadataClassifierField";
import {
    ClassifierOutput,
    ExtendedMetadataClassifier,
} from "models/ClassifierModel";
// Trailing-function usage is disabled for now (SQLJsonUtilities removed from AcxHelpers); re-enable
// alongside JsonSchemaPathDropdownPicker's function step.
// import { JsonPathFunction } from "models/JsonPathFunction";
import ClassifierService from "services/ClassifierService";
import QuestionMetadataService, {
    QuestionMetadataRecord,
    QuestionMetadataRequest,
} from "services/QuestionMetadataService";
import { useStore } from "utils/useStore";

const selectCustomStyle = {
    menuPortal: (provided) => ({ ...provided, zIndex: 10000 }),
};

const noSelectionId = "no-selection";

type MetadataFieldOption = {
    id: string;
    name: string;
    fieldName: string;
    metadataType?: MetadataType;
    isGroupable?: boolean;
};

interface OrganizationModuleQuestionMetadataFormProps {
    organizationId?: string;
    question?: Question;
    // Reports why the config is invalid (used as the Apply tooltip), or null when valid.
    onValidityChange?: (invalidReason: string | null) => void;
    // Reports once the user has edited any selection since load (one-way; never resets on revert).
    onDirtyChange?: (dirty: boolean) => void;
}

export interface QuestionMetadataFormHandle {
    // Builds the metadata payload from current selections, or null if nothing valid is selected.
    getPayload: () => QuestionMetadataRequest | null;
}

const OrganizationModuleQuestionMetadataForm = React.forwardRef<
    QuestionMetadataFormHandle,
    OrganizationModuleQuestionMetadataFormProps
>((props, ref) => {
    const service = React.useMemo(() => new QuestionMetadataService(), []);
    const classifierService = React.useMemo(() => new ClassifierService(), []);
    const messageStore = useStore(MessageStore);
    const [loading, setLoading] = React.useState(true);
    const [metadataFields, setMetadataFields] = React.useState<
        MetadataFieldOption[]
    >([]);
    const [selectedMetadataField, setSelectedMetadataField] = React.useState<
        MetadataFieldOption | undefined
    >(undefined);
    const [questionsMetadata, setQuestionsMetadata] = React.useState<
        QuestionMetadataRecord[]
    >([]);
    const [metadataValueTagMap, setMetadataValueTagMap] = React.useState<
        { metaValue: string; tagId: string }[]
    >([]);
    const [selectedFieldPath, setSelectedFieldPath] = React.useState<
        string | null
    >(null);
    const [dirty, setDirty] = React.useState(false);
    const markDirty = React.useCallback(() => setDirty(true), []);
    const [classifierSchemaMap, setClassifierSchemaMap] = React.useState<
        Map<string, JsonSchemaRoot>
    >(new Map());
    const [classifierOutputs, setClassifierOutputs] = React.useState<
        ClassifierOutput[]
    >([]);
    const [classifierOutputsLoading, setClassifierOutputsLoading] =
        React.useState(false);
    // Commented out as functions will be added but aren't supported by helpers yet.
    // const [availableJsonPathFunctions, setAvailableJsonPathFunctions] =
    //     React.useState<JsonPathFunction[]>([]);

    const createMetadataValueTagMapFromResponse = React.useCallback(
        (response: Record<string, string>) =>
            Object.keys(response).map((key) => ({
                metaValue: key,
                tagId: response[key],
            })),
        [],
    );

    React.useEffect(() => {
        const getMetadataFields = async () => {
            setLoading(true);

            try {
                const [
                    availableFields,
                    extendedMetadataClassifiers,
                    questionMetadataResponse,
                    // jsonPathFunctions,
                ] = await Promise.all([
                    service.getAvailableMetadataClassifierFields(
                        props.organizationId ?? "",
                    ),
                    classifierService.getExtendedMetadataObjectClassifiers(),
                    service.getQuestionMetadata(props.organizationId),
                    // service.getAvailableJsonPathFunctions(),
                ]);

                const mappedFields: MetadataFieldOption[] = availableFields
                    .map((field) => ({
                        id: field.classifierId,
                        name: field.label,
                        fieldName: field.fieldName,
                        metadataType: field.type,
                        isGroupable: field.classifier?.isGroupable === true,
                    }))
                    .sort((a, b) =>
                        a.name
                            .toLocaleLowerCase()
                            .localeCompare(b.name.toLocaleLowerCase()),
                    );

                setMetadataFields(mappedFields);
                setQuestionsMetadata(questionMetadataResponse);
                // setAvailableJsonPathFunctions(jsonPathFunctions);
                setClassifierSchemaMap(
                    new Map(
                        extendedMetadataClassifiers
                            .filter(
                                (
                                    classifier,
                                ): classifier is ExtendedMetadataClassifier & {
                                    jsonSchema: JsonSchemaRoot;
                                } => !!classifier.jsonSchema,
                            )
                            .map((classifier) => [
                                classifier.id,
                                classifier.jsonSchema,
                            ]),
                    ),
                );
            } catch {
                messageStore.logError("Failed to load question metadata");
            } finally {
                setLoading(false);
            }
        };

        getMetadataFields();
    }, [classifierService, messageStore, props.organizationId, service]);

    const questionToUpdate = React.useMemo(
        () =>
            questionsMetadata.find(
                (data) => data.questionId === props.question?.id,
            ),
        [props.question?.id, questionsMetadata],
    );

    React.useEffect(() => {
        if (!questionToUpdate) {
            setSelectedMetadataField(undefined);
            setSelectedFieldPath(null);
            setMetadataValueTagMap([]);
            return;
        }

        const matchingField = metadataFields.find(
            (field) =>
                field.id === questionToUpdate.metadataClassifierId ||
                field.fieldName === questionToUpdate.metadataName,
        );

        if (matchingField) {
            setSelectedMetadataField(matchingField);
        } else if (
            questionToUpdate.metadataClassifierId &&
            questionToUpdate.metadataType !== undefined
        ) {
            setSelectedMetadataField({
                id: questionToUpdate.metadataClassifierId,
                name: questionToUpdate.metadataName ?? "Unknown field",
                fieldName: questionToUpdate.metadataName ?? "",
                metadataType: questionToUpdate.metadataType,
            });
        } else {
            setSelectedMetadataField(undefined);
        }

        setSelectedFieldPath(
            questionToUpdate.metadataType === MetadataType.ExtendedObject
                ? questionToUpdate.fieldPath ?? null
                : null,
        );
        setMetadataValueTagMap(
            questionToUpdate.metadataValueTagMap
                ? createMetadataValueTagMapFromResponse(
                      questionToUpdate.metadataValueTagMap,
                  )
                : [],
        );
    }, [
        createMetadataValueTagMapFromResponse,
        metadataFields,
        questionToUpdate,
    ]);

    const metadataFieldOptions: any[] = React.useMemo(() => {
        const metadataTypeLabels = {
            [MetadataType.Standard]: "Standard",
            [MetadataType.Extended]: "Extended",
            [MetadataType.ExtendedObject]: "Extended Object",
        };

        return Object.values(MetadataType)
            .filter((value): value is MetadataType => typeof value === "number")
            .map((metadataType) => ({
                label: metadataTypeLabels[metadataType],
                options: metadataFields.filter(
                    (field) => field.metadataType === metadataType,
                ),
            }))
            .filter((group) => group.options.length > 0);
    }, [metadataFields]);

    const selectedSchema = React.useMemo(() => {
        if (
            selectedMetadataField?.metadataType !== MetadataType.ExtendedObject
        ) {
            return undefined;
        }

        return classifierSchemaMap.get(selectedMetadataField.id);
    }, [classifierSchemaMap, selectedMetadataField]);

    const isExtendedObjectSelection =
        selectedMetadataField?.metadataType === MetadataType.ExtendedObject;

    const isGroupableSelection =
        selectedMetadataField?.isGroupable === true &&
        !!selectedMetadataField?.id;

    React.useEffect(() => {
        if (!isGroupableSelection) {
            setClassifierOutputs([]);
            return;
        }

        let cancelled = false;
        const classifierId = selectedMetadataField!.id;

        const loadOutputs = async () => {
            setClassifierOutputsLoading(true);
            try {
                const outputs = await classifierService.getClassifierOutputs(
                    classifierId,
                );
                if (!cancelled) {
                    setClassifierOutputs(outputs);
                }
            } catch {
                if (!cancelled) {
                    setClassifierOutputs([]);
                    messageStore.logError(
                        "Failed to load classifier output values",
                    );
                }
            } finally {
                if (!cancelled) {
                    setClassifierOutputsLoading(false);
                }
            }
        };

        loadOutputs();

        return () => {
            cancelled = true;
        };
    }, [
        classifierService,
        isGroupableSelection,
        messageStore,
        selectedMetadataField,
    ]);

    const normalizedMetaValues = metadataValueTagMap
        .map((item) => (item.metaValue ?? "").trim().toLowerCase())
        .filter((value) => value.length > 0);
    const hasDuplicates =
        new Set(normalizedMetaValues).size !== normalizedMetaValues.length;

    // A metadata config requires a selected field, complete value/tag pairs (no dupes), and a field
    // path when targeting an Extended Object. Saving/clearing is orchestrated by the parent drawer.
    const invalidReason = !selectedMetadataField
        ? "Select a metadata field"
        : metadataValueTagMap.some((item) => !item.metaValue || !item.tagId)
          ? "Complete every metadata value and tag"
          : hasDuplicates
            ? "Remove duplicate metadata values"
            : isExtendedObjectSelection && !selectedFieldPath
              ? "Select a field path"
              : null;

    const { onValidityChange, onDirtyChange } = props;
    React.useEffect(() => {
        onValidityChange?.(invalidReason);
    }, [invalidReason, onValidityChange]);

    React.useEffect(() => {
        onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);

    const buildPayload =
        React.useCallback((): QuestionMetadataRequest | null => {
            if (
                !selectedMetadataField ||
                selectedMetadataField.metadataType === undefined
            ) {
                return null;
            }

            const formattedMetadataValueTagMap =
                metadataValueTagMap.length > 0
                    ? metadataValueTagMap.reduce<Record<string, string>>(
                          (accumulator, item) => {
                              const key = item.metaValue.trim();
                              if (key) {
                                  accumulator[key] = item.tagId;
                              }
                              return accumulator;
                          },
                          {},
                      )
                    : null;

            return {
                OrganizationId: props.organizationId ?? "",
                MetadataName: selectedMetadataField.fieldName,
                QuestionId: props.question?.id ?? "",
                MetadataType: selectedMetadataField.metadataType,
                MetadataClassifierId: selectedMetadataField.id,
                metadataValueTagMap: formattedMetadataValueTagMap,
                FieldPath: isExtendedObjectSelection ? selectedFieldPath : null,
            };
        }, [
            isExtendedObjectSelection,
            metadataValueTagMap,
            props.organizationId,
            props.question?.id,
            selectedFieldPath,
            selectedMetadataField,
        ]);

    React.useImperativeHandle(ref, () => ({ getPayload: buildPayload }), [
        buildPayload,
    ]);

    return (
        <Grid>
            {loading ? (
                <Grid container item xs={12} style={{ paddingTop: "1rem" }}>
                    <Skeleton width="50%" height={24} />
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={40}
                        sx={{ borderRadius: 1, marginBottom: 2 }}
                    />
                    {[...Array(2)].map((_, index) => (
                        <Grid
                            container
                            item
                            xs={12}
                            spacing={1}
                            key={index}
                            sx={{ marginBottom: 1 }}
                        >
                            <Grid item xs={6}>
                                <Skeleton
                                    variant="rectangular"
                                    width="100%"
                                    height={40}
                                    sx={{ borderRadius: 1 }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Skeleton
                                    variant="rectangular"
                                    width="100%"
                                    height={40}
                                    sx={{ borderRadius: 1 }}
                                />
                            </Grid>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <>
                    <Grid
                        container
                        item
                        xs={12}
                        alignItems="center"
                        style={{ paddingTop: "1rem" }}
                    >
                        <AcxSelectSingle
                            options={metadataFieldOptions}
                            id="metadataSelect"
                            labelField="name"
                            valueField="id"
                            onChange={(item: any) => {
                                markDirty();
                                if (!item || item.id === noSelectionId) {
                                    setSelectedMetadataField(undefined);
                                    setSelectedFieldPath(null);
                                    setMetadataValueTagMap([]);
                                    return;
                                }

                                setSelectedMetadataField(item);
                                if (
                                    item.metadataType !==
                                        MetadataType.ExtendedObject ||
                                    item.id !== selectedMetadataField?.id
                                ) {
                                    setSelectedFieldPath(null);
                                }
                            }}
                            customStyle={selectCustomStyle}
                            inputLabel="Associated Metadata Field"
                            defaultValue={selectedMetadataField as any}
                        />
                    </Grid>

                    {isExtendedObjectSelection && (
                        <>
                            <Grid
                                container
                                item
                                xs={12}
                                style={{ paddingTop: "1rem" }}
                            >
                                {selectedSchema ? (
                                    <JsonSchemaPathDropdownPicker
                                        key={
                                            selectedMetadataField?.id ??
                                            "no-classifier"
                                        }
                                        schema={selectedSchema}
                                        selectedFieldPath={selectedFieldPath}
                                        onSelectFieldPath={(path) => {
                                            markDirty();
                                            setSelectedFieldPath(path);
                                        }}
                                        // availableFunctions={
                                        //     availableJsonPathFunctions
                                        // }
                                    />
                                ) : (
                                    <Typography color="text.secondary">
                                        No schema defined for this classifier.
                                    </Typography>
                                )}
                            </Grid>

                            <Stack
                                direction="row"
                                alignItems="center"
                                columnGap={1}
                                style={{ paddingTop: "1rem" }}
                            >
                                <Typography>Selected Path</Typography>
                                <AcxMainTextField
                                    fullWidth
                                    id="selected-field-path"
                                    placeholderText="Select a field from above..."
                                    value={selectedFieldPath ?? ""}
                                    readonly
                                    isDisabled
                                />
                            </Stack>
                        </>
                    )}

                    {props.question?.variation?.answerTypeName !==
                        "Question Group" && (
                        <>
                            <Grid
                                container
                                item
                                xs={12}
                                style={{
                                    padding: "1rem",
                                    paddingLeft: 0,
                                    fontWeight: "bold",
                                }}
                            >
                                Metadata Value Tag Map
                            </Grid>
                            {!!metadataValueTagMap.length && (
                                <Grid
                                    container
                                    item
                                    xs={12}
                                    alignItems="center"
                                >
                                    <Grid
                                        container
                                        item
                                        xs={6}
                                        justifyContent="center"
                                    >
                                        Metadata Value
                                    </Grid>
                                    <Grid
                                        container
                                        item
                                        xs={6}
                                        justifyContent="center"
                                    >
                                        Tag
                                    </Grid>
                                </Grid>
                            )}
                            {metadataValueTagMap.map((item, index) => (
                                <Grid
                                    container
                                    item
                                    xs={12}
                                    alignItems="center"
                                    key={index}
                                    spacing={1}
                                >
                                    <Grid item xs={6}>
                                        {isGroupableSelection ? (
                                            <AcxSelectSingle
                                                options={classifierOutputs}
                                                id={
                                                    index +
                                                    "meta-value-output-select"
                                                }
                                                labelField="result"
                                                valueField="result"
                                                customStyle={selectCustomStyle}
                                                isLoading={
                                                    classifierOutputsLoading
                                                }
                                                defaultValue={classifierOutputs.find(
                                                    (output) =>
                                                        output.result ===
                                                        item.metaValue,
                                                )}
                                                onChange={(value: any) => {
                                                    markDirty();
                                                    setMetadataValueTagMap(
                                                        (prev) => {
                                                            prev[
                                                                index
                                                            ].metaValue =
                                                                value?.result ??
                                                                "";
                                                            return [...prev];
                                                        },
                                                    );
                                                }}
                                            />
                                        ) : (
                                            <AcxMainTextField
                                                id={index + "meta-tag"}
                                                type="text"
                                                onChange={(e) => {
                                                    markDirty();
                                                    setMetadataValueTagMap(
                                                        (prev) => {
                                                            prev[
                                                                index
                                                            ].metaValue =
                                                                e.target.value;
                                                            prev = [...prev];
                                                            return prev;
                                                        },
                                                    );
                                                }}
                                                value={item.metaValue}
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={6}>
                                        <AcxSelectSingle
                                            options={props.question?.tags ?? []}
                                            id="ext-metadata-tag-select"
                                            labelField="value"
                                            valueField="id"
                                            customStyle={selectCustomStyle}
                                            defaultValue={props.question?.tags?.find(
                                                (tag) => tag.id === item.tagId,
                                            )}
                                            onChange={(value) => {
                                                markDirty();
                                                setMetadataValueTagMap(
                                                    (prev) => {
                                                        prev[index].tagId =
                                                            value.id;
                                                        prev = [...prev];
                                                        return prev;
                                                    },
                                                );
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            ))}
                            <Grid
                                container
                                item
                                xs={12}
                                justifyContent="space-between"
                                style={{ paddingTop: "1rem" }}
                            >
                                <Grid>
                                    <AcxButton
                                        fullWidth
                                        onClick={() => {
                                            markDirty();
                                            setMetadataValueTagMap((prev) => [
                                                ...prev,
                                                { metaValue: "", tagId: "" },
                                            ]);
                                        }}
                                        color="primary"
                                    >
                                        Add
                                    </AcxButton>
                                </Grid>
                                {!!metadataValueTagMap.length && (
                                    <Grid>
                                        <AcxButton
                                            fullWidth
                                            onClick={() => {
                                                markDirty();
                                                setMetadataValueTagMap((prev) =>
                                                    prev.slice(0, -1),
                                                );
                                            }}
                                        >
                                            Remove
                                        </AcxButton>
                                    </Grid>
                                )}
                            </Grid>
                            {hasDuplicates && (
                                <Grid item xs={12}>
                                    <Typography color="error">
                                        No duplicate meta values allowed
                                    </Typography>
                                </Grid>
                            )}
                            {isExtendedObjectSelection &&
                                !selectedFieldPath &&
                                !!selectedSchema && (
                                    <Grid item xs={12}>
                                        <Typography color="error">
                                            Select a field path before applying
                                            changes
                                        </Typography>
                                    </Grid>
                                )}
                        </>
                    )}
                </>
            )}
        </Grid>
    );
});

OrganizationModuleQuestionMetadataForm.displayName =
    "OrganizationModuleQuestionMetadataForm";

export default OrganizationModuleQuestionMetadataForm;
